import { useEffect, useRef, useState } from 'react';

const isHtmlContent = (value = '') => /<[^>]+>/.test(value);

const sanitizeHtml = (html = '') => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script, style, iframe, object, embed').forEach(node => node.remove());
  doc.body.querySelectorAll('*').forEach((node) => {
    [...node.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value.toLowerCase();
      const allowedStyle = name === 'style' && /^\s*text-align\s*:\s*(left|center|right)\s*;?\s*$/i.test(attr.value);
      if (name.startsWith('on') || value.includes('javascript:') || (!['b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'div', 'p', 'br'].includes(node.tagName.toLowerCase()) && name !== 'style')) {
        node.removeAttribute(attr.name);
      } else if (name === 'style' && !allowedStyle) {
        node.removeAttribute(attr.name);
      }
    });
  });
  return doc.body.innerHTML;
};

const plainTextToHtml = (value = '') => {
  if (!value) return '';
  if (isHtmlContent(value)) return sanitizeHtml(value);
  return value
    .split('\n')
    .map(line => `<div>${line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') || '<br>'}</div>`)
    .join('');
};

export const stripHtml = (value = '') => {
  if (!value) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = plainTextToHtml(value);
  return (tmp.textContent || tmp.innerText || '').replace(/\u00a0/g, ' ').trim();
};

export function RichTextEditor({ value, onChange, placeholder, minHeight = 150, testId = 'rich-text-editor' }) {
  const editorRef = useRef(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused && editorRef.current && editorRef.current.innerHTML !== plainTextToHtml(value || '')) {
      editorRef.current.innerHTML = plainTextToHtml(value || '');
    }
  }, [value, focused]);

  const emitChange = () => {
    const html = editorRef.current?.innerHTML || '';
    onChange(sanitizeHtml(html));
  };

  const runCommand = (command, commandValue = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  const toolbarButton = (label, command, extraClass = '', commandValue = null) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => runCommand(command, commandValue)}
      className={`h-8 min-w-8 px-2 rounded border border-gray-200 bg-white text-sm font-bold text-gray-700 hover:bg-gray-50 ${extraClass}`}
      data-testid={`${testId}-${command}${commandValue ? `-${commandValue}` : ''}`}
      aria-label={label}
    >
      {label}
    </button>
  );

  return (
    <div className="rounded-lg border border-gray-300 bg-white overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 px-2 py-2">
        {toolbarButton('B', 'bold')}
        {toolbarButton('I', 'italic', 'italic')}
        {toolbarButton('U', 'underline', 'underline')}
        <span className="mx-1 h-5 w-px bg-gray-200" />
        {toolbarButton('• Lista', 'insertUnorderedList', 'font-normal')}
        {toolbarButton('1. Lista', 'insertOrderedList', 'font-normal')}
        <span className="mx-1 h-5 w-px bg-gray-200" />
        {toolbarButton('←', 'justifyLeft')}
        {toolbarButton('↔', 'justifyCenter')}
        {toolbarButton('→', 'justifyRight')}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        onInput={emitChange}
        onBlur={() => { setFocused(false); emitChange(); }}
        onFocus={() => setFocused(true)}
        data-placeholder={placeholder}
        className="rich-text-content w-full px-3 py-3 text-sm text-gray-900 focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
        style={{ minHeight }}
        data-testid={`${testId}-content`}
      />
    </div>
  );
}

export function RichTextDisplay({ value, className = '', testId }) {
  return (
    <div
      className={`rich-text-display whitespace-normal break-words ${className}`}
      data-testid={testId}
      dangerouslySetInnerHTML={{ __html: plainTextToHtml(value || '') }}
    />
  );
}